import type { StandardSchemaV1 } from '@standard-schema/spec';
import type { Component } from 'vue';
import type { useI18n } from 'vue-i18n';
import type { ValueOf } from '#layers/base/public';
import type { FormSchema, FormSchemaResolved } from '#layers/block-engine/public';
import type { PrimitiveTypeName } from '#layers/data-model/types/schema/primitive-type';
import type { ConnectorAvailableSince } from '#layers/data-model/utils/connector-availability';
import type { TranslationPlaceholder } from '#layers/i18n/public';

export const ConnectorType = {
	Relational: 'relational',
	Extension: 'extension',
} as const;

export type ConnectorType = ValueOf<typeof ConnectorType>;

/**
 * Helper to extract BaseOptions from an extended connector.
 * Uses the phantom '~options' property to avoid circular type inference.
 */
type GetBaseOptions<TOptions, TExtends> = TExtends extends { '~options': infer BaseOpts }
	? BaseOpts extends Record<string, any> ? BaseOpts : TOptions
	: TOptions;

/**
 * Context available in the FormSchema getter when the connector extends a base.
 * Provides access to the base connector's resolved options for the current form values.
 */
export interface ConnectorFormSchemaContext<TBaseOptions extends Record<string, any> = Record<string, any>> {
	base?: {
		/** Base connector's options resolved for current form values */
		resolvedOptions: FormSchemaResolved<TBaseOptions>;
		/** Base connector's validation schema */
		schema: StandardSchemaV1 | undefined;
	};
	i18n: ReturnType<typeof useI18n>;
	/**
	 * True while a base connector's options are being resolved to build a
	 * preset's `base.resolvedOptions`. Base connectors read it to skip injecting
	 * auth controls the preset owns, so those controls never leak into a preset's
	 * form or double-inject on the AWS presets.
	 */
	resolvingAsBase?: boolean;
}

/**
 * Helpers passed to onOptionsChange callback.
 */
export interface OnOptionsChangeHelpers<TOptions> {
	/** Get current form values */
	getValues: () => TOptions;
	/** Get the connector form's default options */
	getDefaultOptions: () => Partial<TOptions>;
	/** Update form values (triggers reactivity) */
	setValues: (partial: Partial<TOptions>) => void;
	/** Set or clear a field error (blocks form submission) */
	setFieldError: (field: keyof TOptions | string, error: string | null) => void;
	/** Set or clear a field warning (advisory; does NOT block submission) */
	setFieldWarning: (field: keyof TOptions | string, warning: string | null) => void;
	/** Check if a specific field changed between old and new values */
	hasChanged: (field: keyof TOptions) => boolean;
	/** Resolve a field's translated, human-readable label from the form schema; undefined if unknown. */
	getFieldLabel: (field: keyof TOptions | string) => string | undefined;
}

export type ConnectorOnOptionsChange<TOptions> = (
	newValues: TOptions,
	oldValues: TOptions,
	helpers: OnOptionsChangeHelpers<TOptions>,
) => void;

/** A validation error bound to a form field, returned by {@link ConnectorConfig.validateOptions}. */
export interface ConnectorOptionError {
	/** Field name (dot-path for nested fields, for example `aws_iam.region`). */
	name: string;
	/** Message or translation placeholder shown on the field. */
	message: string;
}

export interface ConnectorFormSuggestionContext<TOptions extends Record<string, any> = Record<string, any>> {
	values: Partial<TOptions>;
}

export interface ConnectorFormSuggestion {
	type: 'switch-connector';
	color: 'warning';
	title: TranslationPlaceholder;
	description?: TranslationPlaceholder;
	actionLabel: TranslationPlaceholder;
	connector: string;
}

/**
 * Provide a list of form fields that will be used as the connector options form.
 *
 * If the output of these options is different from the [TOptions] you also need to provide
 * [transformOptions] that transforms them into the standard structure.
 */
interface ConnectorOptionFields<TOptions extends Record<string, any>, TBaseOptions extends Record<string, any>> {
	/**
	 * Form fields for the connector options.
	 * Can be:
	 * - A static array of form fields
	 * - A FormSchema getter function receiving `{ form, values, context }`
	 *
	 * When extending a base connector, `context.base.resolvedOptions` provides the base's
	 * form fields already resolved for the current form values.
	 *
	 * @example
	 * // Static array
	 * options: [{ name: 'host', block: BuiltinBlockIds.Input }]
	 *
	 * @example
	 * // Dynamic fields based on values
	 * options: ({ form, values }) => form([
	 *   { name: 'ssl.mode', ... },
	 *   ...(values.ssl?.mode === 'require' ? [{ name: 'ssl.ca_cert', ... }] : []),
	 * ])
	 *
	 * @example
	 * // Preset extending base connector
	 * options: ({ form, context }) => form(
	 *   context.base.resolvedOptions.map(field =>
	 *     field.name === 'connectionString'
	 *       ? { ...field, props: { ...field.props, placeholder: 'custom://...' } }
	 *       : field
	 *   )
	 * )
	 */
	options?: FormSchema<TOptions, ConnectorFormSchemaContext<TBaseOptions>>;
	optionsComponent?: never;

	/**
	 * Default values for the connector create options.
	 *
	 * Unused. Will be inferred from the options.
	 */
	defaultOptions?: never;

	transformOptions?: (options: TOptions) => TBaseOptions;
}

interface ConnectorOptionComponent<TOptions extends Record<string, any>, TBaseOptions extends Record<string, any>> {
	options?: never;
	optionsComponent: Component<TOptions>;
	defaultOptions?: Partial<TOptions>;

	transformOptions?: (options: TOptions) => TBaseOptions;
}

/**
 * Only provide default options.
 *
 * Use this if the connector does not provide any special configuration options.
 */
interface ConnectorOptionDefault<TOptions> {
	options?: never;
	optionsComponent?: never;
	defaultOptions: Partial<TOptions>;

	transformOptions?: never;
}

/** Nuxt UI theme color used for an advisory notice. */
export type ConnectorNoticeColor = 'info' | 'success' | 'warning' | 'error' | 'neutral';
export type ConnectorSupportLevel = 'alpha' | 'beta';

export function connectorSupportLevelColor(supportLevel: ConnectorSupportLevel) {
	switch (supportLevel) {
		case 'alpha':
			return 'warning';
		case 'beta':
			return 'neutral';
	}
}

/**
 * A contextual advisory notice declared on a connector and rendered in the
 * options form's advisory region.
 */
export interface ConnectorNotice<TOptions extends Record<string, any> = Record<string, any>> {
	/** @default 'info' */
	color?: ConnectorNoticeColor;
	title: TranslationPlaceholder;
	description?: TranslationPlaceholder;
	/** Only show the notice when this predicate returns true. Absent = always shown. */
	when?: (values: Partial<TOptions>) => boolean;
}

export type ConnectorConfig<
	TOptions extends Record<string, any>,
	TExtends extends ConnectorDefinition<any, any> | undefined,
> = {
	id: string;
	/**
	 * Backend provider ID used when creating or testing a source.
	 *
	 * Defaults to this connector's id. For inherited connectors, defaults to
	 * the resolved base connector provider unless explicitly overridden.
	 */
	provider?: string;
	name: TranslationPlaceholder;
	description: TranslationPlaceholder;

	type: ConnectorType;
	sort?: number;

	/** Date when this connector was introduced. Used to derive availability badges. */
	availableSince?: ConnectorAvailableSince;

	/**
	 * Icon name or SVG string that can be used instead of the icon
	 */
	icon?: string;
	iconColor?: string;

	/**
	 * Validation schema for form input.
	 */
	schema?: StandardSchemaV1;

	// TODO this needs to be distributed by the actual connector capabilities and not defined in the frontend
	supportedPrimitiveTypes?: PrimitiveTypeName[];

	/**
	 * Extend a base connector. When set:
	 * - Options can be a function receiving base connector context
	 * - Schema is inherited if not specified
	 * - Keywords are merged with base connector's keywords
	 * - Submission uses base connector's ID as provider
	 */
	extends?: TExtends;

	/**
	 * Search keywords for finding this connector.
	 * Merged with base connector's keywords when using `extends`.
	 */
	keywords?: string[];

	/** Provider documentation surfaced to the user. */
	docsUrl?: string;

	/** Support maturity for wire-compatible / not-yet-first-party-tested providers. Absent = stable. */
	supportLevel?: ConnectorSupportLevel;

	/**
	 * Contextual advisory notices surfaced in the connector's options form.
	 * Rendered in the form's advisory region (alongside field warnings and
	 * connector suggestions), not interleaved with the form fields.
	 */
	notices?: ConnectorNotice<TOptions>[];

	/**
	 * Callback fired when form options change.
	 * Use for features like connection string parsing.
	 */
	onOptionsChange?: ConnectorOnOptionsChange<TOptions>;

	/**
	 * Submit-time validation of the raw form values, run in addition to
	 * {@link schema}. Use for cross-field rules that depend on form-only
	 * discriminants absent from the wire schema (for example, which credentials
	 * an AWS IAM mode requires). Unlike {@link onOptionsChange}, this runs
	 * deterministically at submit regardless of the live change handler, so
	 * returning a non-empty list blocks submission. Return `[]` when valid.
	 */
	validateOptions?: (options: TOptions) => ConnectorOptionError[];

	/**
	 * Optional high-level suggestion derived from the current form values.
	 *
	 * Connectors own the detection rule; the source form owns rendering and navigation.
	 */
	getFormSuggestion?: (context: ConnectorFormSuggestionContext<TOptions>) => ConnectorFormSuggestion | undefined;
} & (
	| ConnectorOptionFields<TOptions, GetBaseOptions<TOptions, TExtends>>
	| ConnectorOptionComponent<TOptions, GetBaseOptions<TOptions, TExtends>>
	| ConnectorOptionDefault<TOptions>
	);

/**
 * The Full Definition (Output).
 * Includes the phantom '~options' property for type propagation.
 */
export type ConnectorDefinition<
	TOptions extends Record<string, any> = any,
	TExtends extends ConnectorDefinition<any, any> | undefined = undefined,
> = ConnectorConfig<TOptions, TExtends> & {
	'~options': TOptions;
};

/**
 * Resolved connector definition after inheritance is applied.
 */
export interface ResolvedConnectorDefinition<TOptions extends Record<string, any> = Record<string, unknown>>
	extends Omit<ConnectorDefinition<TOptions>, 'extends' | 'options'> {
	/** Form schema (static or getter function). Resolve with live values using resolveFormSchema. */
	options?: FormSchema<TOptions, ConnectorFormSchemaContext<any>>;
	/** Base connector's raw options (for resolving context.base.resolvedOptions) */
	baseOptions?: FormSchema<any, ConnectorFormSchemaContext<any>>;
	/** All keywords including inherited ones */
	allKeywords: string[];
	/** Provider ID for submission (base connector's ID when using extends) */
	provider: string;
	/** Backend preset value to persist when this connector is selected. */
	preset: string | null;
	/** Whether this connector owns metadata lookup for its backend provider */
	providerMetadata?: boolean;
	/** Base schema for validating transformOptions output */
	baseSchema?: StandardSchemaV1;
}

/**
 * Define a connector that extends a base connector.
 * TExtends is inferred from the `extends` property, TOptions defaults to base's options type.
 */
export function defineConnector<
	TExtends extends ConnectorDefinition<any, any>,
	TOptions extends Record<string, any> = TExtends['~options'],
>(
	connector: ConnectorConfig<TOptions, TExtends> & { extends: TExtends },
): ConnectorDefinition<TOptions, TExtends>;

/**
 * Define a base connector (no extends).
 */
export function defineConnector<TOptions extends Record<string, any>>(
	connector: ConnectorConfig<TOptions, undefined> & { extends?: never },
): ConnectorDefinition<TOptions>;

// Implementation
export function defineConnector(connector: any): any {
	return connector;
}
